import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  AlertCircle,
  CheckCircle,
  Loader,
  Trophy,
  Users
} from 'lucide-react';

const MatchForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const [formData, setFormData] = useState({
    team: userProfile?.role === 'entrenador' ? userProfile.team : 'Juvenil',
    date: new Date().toISOString().split('T')[0],
    start_time: '10:00',
    end_time: '',
    opponent: '',
    competition: '',
    location: '',
    match_type: 'amistoso',
    is_home: true,
    our_score: '',
    opponent_score: '',
    tactical_notes: '',
    formation: '',
    strategic_changes: '',
    observations: ''
  });

  const teams = ['Juvenil', 'Cadete', 'Amateur'];

  useEffect(() => {
    if (id) {
      setIsEditMode(true);
      fetchMatch();
    }
  }, [id]);

  const fetchMatch = async () => {
    try {
      setLoadingData(true);
      const { data, error } = await supabase
        .from('matches')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      if (data) {
        setFormData({
          team: data.team,
          date: data.date,
          start_time: data.start_time,
          end_time: data.end_time || '',
          opponent: data.opponent,
          competition: data.competition || '',
          location: data.location || '',
          match_type: data.match_type,
          is_home: data.is_home,
          our_score: data.our_score !== null ? data.our_score : '',
          opponent_score: data.opponent_score !== null ? data.opponent_score : '',
          tactical_notes: data.tactical_notes || '',
          formation: data.formation || '',
          strategic_changes: data.strategic_changes || '',
          observations: data.observations || ''
        });
      }
    } catch (err) {
      console.error('Error al cargar partido:', err);
      setError('Error al cargar los datos del partido');
    } finally {
      setLoadingData(false);
    }
  };
  const matchTypes = [
    { value: 'amistoso', label: 'Amistoso' },
    { value: 'oficial', label: 'Oficial' },
    { value: 'copa', label: 'Copa' },
    { value: 'torneo', label: 'Torneo' }
  ];

  const formations = [
    '4-4-2',
    '4-3-3',
    '4-2-3-1',
    '3-5-2',
    '3-4-3',
    '5-3-2',
    '4-1-4-1',
    '4-5-1'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validaciones
      if (!formData.date) {
        throw new Error('La fecha es obligatoria');
      }

      if (!formData.opponent) {
        throw new Error('El rival es obligatorio');
      }

      if (!formData.start_time) {
        throw new Error('La hora de inicio es obligatoria');
      }

      const matchData = {
        team: formData.team,
        date: formData.date,
        start_time: formData.start_time,
        end_time: formData.end_time || null,
        opponent: formData.opponent,
        competition: formData.competition || null,
        location: formData.location || null,
        match_type: formData.match_type,
        is_home: formData.is_home,
        our_score: formData.our_score !== '' ? parseInt(formData.our_score) : 0,
        opponent_score: formData.opponent_score !== '' ? parseInt(formData.opponent_score) : 0,
        tactical_notes: formData.tactical_notes || null,
        formation: formData.formation || null,
        strategic_changes: formData.strategic_changes || null,
        observations: formData.observations || null
      };

      if (!isEditMode) {
        matchData.created_by = userProfile.id;
      }

      const { error: saveError } = isEditMode
        ? await supabase.from('matches').update(matchData).eq('id', id)
        : await supabase.from('matches').insert([matchData]);

      if (saveError) throw saveError;

      setSuccess(true);

      setTimeout(() => {
        navigate('/matches');
      }, 2000);

    } catch (err) {
      console.error('Error al guardar partido:', err);
      setError(err.message || `Error al ${isEditMode ? 'actualizar' : 'crear'} el partido`);
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando partido...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {isEditMode ? '¡Partido actualizado!' : '¡Partido creado!'}
          </h2>
          <p className="text-gray-600 mb-4">
            {isEditMode ? 'Los cambios se han guardado correctamente' : 'El partido se ha registrado correctamente'}
          </p>
          <button
            onClick={() => navigate('/matches')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Volver a Partidos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/matches')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditMode ? 'Editar Partido' : 'Nuevo Partido'}
              </h1>
              <p className="text-sm text-gray-600">
                {isEditMode ? 'Modifica la información del partido' : 'Registra un nuevo partido'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Información básica */}
          <div className="bg-white rounded-xl shadow-sm p-8">
            <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Trophy className="w-5 h-5" />
              Información Básica
            </h3>

            <div className="space-y-6">
              {/* Equipo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Equipo <span className="text-red-500">*</span>
                </label>
                <select
                  name="team"
                  value={formData.team}
                  onChange={handleInputChange}
                  required
                  disabled={userProfile?.role === 'entrenador'}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                >
                  {teams.map(team => (
                    <option key={team} value={team}>{team}</option>
                  ))}
                </select>
              </div>

              {/* Rival */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rival <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="opponent"
                  value={formData.opponent}
                  onChange={handleInputChange}
                  required
                  placeholder="Ej: CD Alcoi, Valencia CF..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Tipo de partido */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de partido <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="match_type"
                    value={formData.match_type}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {matchTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                {/* Competición */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Competición
                  </label>
                  <input
                    type="text"
                    name="competition"
                    value={formData.competition}
                    onChange={handleInputChange}
                    placeholder="Ej: Liga Regional, Copa FFCV..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Fecha */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Fecha <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Hora inicio */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Hora de inicio <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    name="start_time"
                    value={formData.start_time}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Ubicación */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Campo / Ubicación
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="Ej: Polideportivo Municipal"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Local/Visitante */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo
                  </label>
                  <div className="flex items-center gap-6 h-[42px]">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="is_home"
                        checked={formData.is_home === true}
                        onChange={() => setFormData(prev => ({ ...prev, is_home: true }))}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-sm text-gray-700">🏠 Local</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="is_home"
                        checked={formData.is_home === false}
                        onChange={() => setFormData(prev => ({ ...prev, is_home: false }))}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-sm text-gray-700">✈️ Visitante</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Resultado */}
          <div className="bg-white rounded-xl shadow-sm p-8">
            <h3 className="text-lg font-bold text-gray-900 mb-6">
              ⚽ Resultado
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Goles a favor
                </label>
                <input
                  type="number"
                  name="our_score"
                  value={formData.our_score}
                  onChange={handleInputChange}
                  min="0"
                  placeholder="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Goles en contra
                </label>
                <input
                  type="number"
                  name="opponent_score"
                  value={formData.opponent_score}
                  onChange={handleInputChange}
                  min="0"
                  placeholder="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Información táctica */}
          <div className="bg-white rounded-xl shadow-sm p-8">
            <h3 className="text-lg font-bold text-gray-900 mb-6">
              📋 Información Táctica
            </h3>

            <div className="space-y-6">
              {/* Formación */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Formación / Esquema táctico
                </label>
                <select
                  name="formation"
                  value={formData.formation}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Sin especificar</option>
                  {formations.map(formation => (
                    <option key={formation} value={formation}>{formation}</option>
                  ))}
                </select>
              </div>

              {/* Notas tácticas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notas tácticas
                </label>
                <textarea
                  name="tactical_notes"
                  value={formData.tactical_notes}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Plan de juego, estrategia, puntos clave..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Cambios estratégicos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cambios estratégicos
                </label>
                <textarea
                  name="strategic_changes"
                  value={formData.strategic_changes}
                  onChange={handleInputChange}
                  rows="2"
                  placeholder="Cambios realizados durante el partido, ajustes tácticos..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Observaciones */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Observaciones generales
                </label>
                <textarea
                  name="observations"
                  value={formData.observations}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Conclusiones, aspectos a mejorar, puntos positivos..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Botones */}
          <div className="flex items-center justify-end gap-4 bg-white rounded-xl shadow-sm p-6">
            <button
              type="button"
              onClick={() => navigate('/matches')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading && <Loader className="w-4 h-4 animate-spin" />}
              {loading
                ? (isEditMode ? 'Guardando...' : 'Creando...')
                : (isEditMode ? 'Guardar Cambios' : 'Crear Partido')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MatchForm;
